import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  MessageSquare,
  Mail,
  Send,
  FileText,
  Sparkles,
  Heart,
  Gift,
  AlertCircle,
  Eye,
  Check,
  Loader
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { getInitials, formatCurrency, formatDate } from '../lib/utils';
import { Customer } from '../types';
import { toast } from 'sonner';
import { sendCustomerMessage } from '../services/customerService';

interface SendMessageModalProps {
  customer: Customer | null;
  isOpen: boolean;
  onClose: () => void;
}

interface MessageTemplate {
  id: string;
  name: string;
  category: string;
  subject: string;
  content: string;
  icon: React.ReactNode;
  tags: string[];
}

const messageTemplates: MessageTemplate[] = [
  {
    id: 'welcome',
    name: 'Welcome Message',
    category: 'Customer Care',
    subject: 'Welcome to JamPack\'d! We\'re thrilled to have you',
    content: `Hi {customerName},

Welcome to the JamPack'd family! We're absolutely thrilled to have you join our community of care package enthusiasts.

At JamPack'd, we believe in spreading joy and connecting hearts through thoughtfully curated care packages. Whether you're sending love to a friend, supporting a family member, or treating yourself, we're here to make every moment special.

Here's what you can expect from us:
• Premium quality products carefully selected for you
• Fast and reliable shipping
• Exceptional customer service
• Exclusive member perks and early access to new products

If you have any questions or need assistance, don't hesitate to reach out. We're always here to help!

Looking forward to being part of your special moments.

Warm regards,`,
    icon: <Heart className="w-4 h-4" />,
    tags: ['welcome', 'new-customer']
  },
  {
    id: 'thank-you',
    name: 'Thank You for Your Order',
    category: 'Customer Care',
    subject: 'Thank you for choosing JamPack\'d!',
    content: `Dear {customerName},

Thank you so much for your recent order with JamPack'd! Your trust in us means the world, and we're excited to help you create meaningful moments.

Your order is now being carefully prepared by our team, and we'll keep you updated every step of the way. We take pride in ensuring every package meets our high standards before it reaches you.

We'd love to hear about your experience! Feel free to share photos and stories on social media and tag us @jampackd - we love seeing the joy our packages bring.

Thank you for being part of the JamPack'd family!

Best regards,`,
    icon: <Heart className="w-4 h-4" />,
    tags: ['thank-you', 'post-purchase']
  },
  {
    id: 'special-offer',
    name: 'Exclusive Offer',
    category: 'Promotional',
    subject: 'Exclusive offer just for you, {customerName}!',
    content: `Hi {customerName},

We have something special just for you! As one of our valued customers, you get exclusive early access to our latest collection.

🎁 Special Offer: 15% off your next order
📦 Free shipping on orders over $75
⭐ First access to limited edition packages

This exclusive offer is our way of saying thank you for being such an important part of the JamPack'd community.

Use code: VIPCARE15 at checkout
Valid until: {nextWeek}

Shop now and spread more joy with JamPack'd!

Happy shopping!`,
    icon: <Gift className="w-4 h-4" />,
    tags: ['promotional', 'exclusive', 'discount']
  },
  {
    id: 'follow-up',
    name: 'Order Follow-up',
    category: 'Customer Care',
    subject: 'How was your JamPack\'d experience?',
    content: `Hi {customerName},

We hope your recent JamPack'd order brought joy to you and your loved ones! 

We'd love to hear about your experience:
• How did you like the products?
• Was everything as expected?
• Is there anything we can improve?

Your feedback helps us continue providing the best possible experience for you and all our customers.

If you have a moment, we'd be grateful if you could leave a review on our website. It really helps other customers discover the joy of JamPack'd!

As always, if you need anything at all, we're just a message away.

With gratitude,`,
    icon: <MessageSquare className="w-4 h-4" />,
    tags: ['follow-up', 'feedback', 'review']
  },
  {
    id: 'support',
    name: 'Customer Support',
    category: 'Support',
    subject: 'We\'re here to help - JamPack\'d Support',
    content: `Dear {customerName},

Thank you for reaching out to us. We've received your inquiry and want you to know that we're here to help resolve any questions or concerns you may have.

Our customer care team is dedicated to providing you with the best possible experience, and we'll make sure to address your needs promptly and thoroughly.

What you can expect:
• A response within 24 hours
• Personalized assistance tailored to your needs
• Follow-up to ensure your satisfaction

Your happiness is our priority, and we appreciate your patience as we work to assist you.

If you have any urgent concerns, please don't hesitate to call us directly.

Best regards,`,
    icon: <AlertCircle className="w-4 h-4" />,
    tags: ['support', 'customer-service']
  },
  {
    id: 'custom',
    name: 'Custom Message',
    category: 'Custom',
    subject: '',
    content: '',
    icon: <FileText className="w-4 h-4" />,
    tags: ['custom']
  }
];

export function SendMessageModal({ customer, isOpen, onClose }: SendMessageModalProps) {
  const [selectedTemplate, setSelectedTemplate] = useState<MessageTemplate | null>(null);
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState<string>('all');
  const [sending, setSending] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [sendPlainText, setSendPlainText] = useState(false);

  // Reset form when modal opens/closes or customer changes
  useEffect(() => {
    if (isOpen && customer) {
      setSelectedTemplate(null);
      setSubject('');
      setContent('');
      setCategory('all');
      setShowPreview(false);
      setSendPlainText(false);
    }
  }, [isOpen, customer]);

  if (!isOpen || !customer) return null;

  const categories = ['all', ...new Set(messageTemplates.map(t => t.category))];

  const filteredTemplates = messageTemplates.filter(template => 
    category === 'all' || template.category === category
  );

  const handleTemplateSelect = (template: MessageTemplate) => {
    setSelectedTemplate(template);
    
    if (template.id === 'custom') {
      setSubject('');
      setContent('');
    } else {
      // Replace placeholders in template
      const processedSubject = template.subject
        .replace(/{customerName}/g, customer.name || 'Valued Customer')
        .replace(/{nextWeek}/g, new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString());
      
      const processedContent = template.content
        .replace(/{customerName}/g, customer.name || 'Valued Customer')
        .replace(/{nextWeek}/g, new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString());
      
      setSubject(processedSubject);
      setContent(processedContent);
    }
  };

  const handleSendMessage = async () => {
    if (!subject.trim() || !content.trim()) {
      toast.error('Please fill in both subject and message content');
      return;
    }

    setSending(true);
    try {
      await sendCustomerMessage({
        customerId: customer.id,
        customerEmail: customer.email,
        customerName: customer.name || 'Valued Customer',
        subject: subject.trim(),
        content: content.trim(),
        template: selectedTemplate?.id || 'custom',
        category: selectedTemplate?.category || 'Custom',
        plainTextOnly: sendPlainText
      });

      toast.success('Message sent successfully!');
      onClose();
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const getCustomerStatus = () => {
    if (typeof customer.isActive === 'boolean') {
      return customer.isActive ? 'active' : 'inactive';
    }
    return 'inactive';
  };

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'active':
        return { color: 'bg-green-100 text-green-800', label: 'Active' };
      case 'inactive':
        return { color: 'bg-gray-100 text-gray-800', label: 'Inactive' };
      default:
        return { color: 'bg-gray-100 text-gray-800', label: 'Unknown' };
    }
  };

  const statusConfig = getStatusConfig(getCustomerStatus());

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-[10001] flex items-center justify-center p-4" style={{ margin: 0, padding: '1rem', top: 0, left: 0, right: 0, bottom: 0 }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ 
            type: "spring",
            duration: 0.5,
            bounce: 0.3 
          }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden"
        >
          {/* Enhanced Header */}
          <div className="relative bg-gradient-to-r from-blue-600 to-blue-500 p-6 flex-shrink-0">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-transparent"></div>
            <div className="relative flex items-center justify-between">
              <div className="flex items-center gap-4">
                <motion.div 
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring", bounce: 0.6 }}
                  className="h-12 w-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30"
                >
                  <MessageSquare className="h-6 w-6 text-white" />
                </motion.div>
                <div>
                  <motion.h2 
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 }}
                    className="text-2xl font-bold text-white"
                  >
                    Send Message to {customer.name}
                  </motion.h2>
                  <motion.div 
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                    className="flex items-center gap-3 text-white/80 text-sm mt-1"
                  >
                    <div className="flex items-center gap-1">
                      <Mail className="w-3 h-3" />
                      {customer.email}
                    </div>
                    <Badge className={statusConfig.color}>
                      {statusConfig.label}
                    </Badge>
                  </motion.div>
                </div>
              </div>
              <motion.div
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={onClose}
                  className="text-white hover:bg-white/20 h-10 w-10 rounded-full"
                >
                  <X className="h-5 w-5" />
                </Button>
              </motion.div>
            </div>
            
            {/* Customer Stats */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="flex items-center gap-6 mt-4 text-sm text-white/80"
            >
              <div className="flex items-center gap-1">
                <span className="font-medium">{customer.orderCount || customer.orderHistory?.length || 0}</span>
                <span>orders</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="font-medium">{formatCurrency(customer.totalSpent || 0)}</span>
                <span>spent</span>
              </div>
              <div className="flex items-center gap-1">
                <span>Member since</span>
                <span className="font-medium">{formatDate(customer.createdAt)}</span>
              </div>
            </motion.div>
          </div>

          {/* Enhanced Content */}
          <div className="flex-1 overflow-hidden flex bg-gradient-to-b from-gray-50/50 to-white">
            {/* Left Side - Template Selection */}
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              className="w-1/3 border-r border-gray-200 flex flex-col"
            >
              <div className="p-6 border-b border-gray-200 bg-white">
                <Label className="text-base font-semibold text-gray-900 flex items-center gap-2 mb-4">
                  <Sparkles className="w-4 h-4 text-blue-600" />
                  Message Templates
                </Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger className="h-12 rounded-xl border-gray-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.slice(1).map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {filteredTemplates.map((template, index) => (
                  <motion.div
                    key={template.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 + index * 0.1 }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Card 
                      className={`cursor-pointer transition-all duration-200 hover:shadow-md ${
                        selectedTemplate?.id === template.id 
                          ? 'ring-2 ring-blue-600 bg-blue-50 shadow-lg' 
                          : 'hover:bg-gray-50'
                      }`}
                      onClick={() => handleTemplateSelect(template)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className={`${selectedTemplate?.id === template.id ? 'text-blue-600' : 'text-gray-400'} mt-0.5`}>
                            {template.icon}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-gray-900 text-sm leading-tight">{template.name}</h4>
                            <p className="text-xs text-gray-500 mt-1">{template.category}</p>
                            {template.tags.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {template.tags.slice(0, 2).map(tag => (
                                  <span key={tag} className={`text-xs px-2 py-1 rounded-full ${
                                    selectedTemplate?.id === template.id ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                                  }`}>
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          {selectedTemplate?.id === template.id && (
                            <motion.div
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              className="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0"
                            >
                              <Check className="w-3 h-3 text-white" />
                            </motion.div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Right Side - Message Composition */}
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              className="flex-1 flex flex-col"
            >
              {selectedTemplate ? (
                <>
                  <div className="p-8 space-y-6 flex-1 overflow-y-auto">
                    <div className="flex items-center justify-between">
                      <Label className="text-base font-semibold text-gray-900 flex items-center gap-2">
                        <FileText className="w-4 text-blue-600" />
                        Compose Message
                      </Label>
                      <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowPreview(!showPreview)}
                          className="flex items-center gap-2 rounded-xl"
                        >
                          <Eye className="w-4 h-4" />
                          {showPreview ? 'Edit' : 'Preview'}
                        </Button>
                      </motion.div>
                    </div>

                    {!showPreview ? (
                      <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="space-y-6"
                      >
                        <div className="space-y-3">
                          <Label htmlFor="subject" className="text-sm font-medium text-gray-900">Subject Line *</Label>
                          <Input
                            id="subject"
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            placeholder="Enter email subject..."
                            className="h-12 rounded-xl border-gray-200 focus:border-blue-600 focus:ring-blue-600/20 shadow-sm hover:shadow-md focus:shadow-lg transition-all duration-200"
                          />
                        </div>

                        <div className="flex items-center justify-between rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
                          <div className="space-y-1">
                            <p className="text-sm font-medium text-gray-900">Send as plain text</p>
                            <p className="text-xs text-gray-500">Delivers without the HTML email template.</p>
                          </div>
                          <Switch checked={sendPlainText} onCheckedChange={setSendPlainText} />
                        </div>

                        <div className="space-y-3 flex-1">
                          <Label htmlFor="content" className="text-sm font-medium text-gray-900">Message Content *</Label>
                          <Textarea
                            id="content"
                            value={content}
                            onChange={(e) => setContent(e.target.value)}
                            placeholder="Enter your message..."
                            className="min-h-[300px] resize-none rounded-xl border-gray-200 focus:border-blue-600 focus:ring-blue-600/20 shadow-sm hover:shadow-md focus:shadow-lg transition-all duration-200"
                          />
                        </div>
                      </motion.div>
                    ) : (
                      <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="space-y-4"
                      >
                        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-100 shadow-sm">
                          <CardHeader className="pb-3">
                            <div className="flex items-center gap-2 text-sm text-blue-600 mb-2">
                              <Mail className="w-4 h-4" />
                              <span className="font-medium">Email Preview</span>
                            </div>
                            <h4 className="font-semibold text-gray-900 text-lg">
                              {subject || 'No subject'}
                            </h4>
                            <div className="flex items-center gap-2 text-sm text-gray-500 mt-2">
                              <span>To: {customer.name} ({customer.email})</span>
                            </div>
                          </CardHeader>
                          <CardContent className="pt-0">
                            <div className="bg-white rounded-lg p-4 border border-blue-100 shadow-sm">
                              <div className="whitespace-pre-wrap text-gray-700 text-sm leading-relaxed">
                                {content || 'No content'}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </motion.div>
                    )}
                  </div>

                  {/* Enhanced Footer */}
                  <div className="p-6 bg-gray-50 border-t border-gray-100 flex-shrink-0">
                    <div className="flex items-center justify-end gap-4">
                      <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                        <Button 
                          variant="outline" 
                          onClick={onClose}
                          disabled={sending}
                          className="px-6 h-11 rounded-xl font-medium"
                        >
                          Cancel
                        </Button>
                      </motion.div>
                      <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                        <Button 
                          onClick={handleSendMessage}
                          disabled={sending || !subject.trim() || !content.trim()}
                          className="bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white px-8 h-11 rounded-xl font-semibold shadow-lg shadow-blue-600/25"
                        >
                          {sending ? (
                            <>
                              <Loader className="w-4 h-4 mr-2 animate-spin" />
                              Sending Message...
                            </>
                          ) : (
                            <>
                              <Send className="w-4 h-4 mr-2" />
                              Send Message
                            </>
                          )}
                        </Button>
                      </motion.div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center p-8">
                  <motion.div 
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.5 }}
                    className="text-center"
                  >
                    <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Select a Template</h3>
                    <p className="text-gray-500">Choose a message template from the left to get started</p>
                  </motion.div>
                </div>
              )}
            </motion.div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
} 
